import { useEffect, useState } from 'react';
import { z } from 'zod';

import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { type ColumnFiltersState } from '@apps/web/lib/table';
import { useUser } from '@apps/web/swr_data_fetching/data_fetching_hooks';

const STORAGE_KEY_BASE = 'phoebe-authorizations-filters';

// Statuses preselected by default, mirroring the backend's
// DEFAULT_DASHBOARD_AUTH_STATUSES: active and pending are the authoritative
// "currently billable" states; closed is included so the dashboard
// reconciles against the source EHR. Rendered as a visible, editable
// include chip.
const DEFAULT_COLUMN_FILTERS: ColumnFiltersState = [
    {
        id: 'statuses',
        value: { include: ['active', 'pending', 'closed'], exclude: [] },
    },
];

const storedFiltersSchema = z.object({
    version: z.literal(1),
    columnFilters: z.array(
        z.object({
            id: z.enum(['locations', 'states', 'statuses', 'service_lines']),
            value: z.object({
                include: z.array(z.string()),
                exclude: z.array(z.string()),
                includeMode: z.enum(['any', 'all']).optional(),
                excludeMode: z.enum(['any', 'all']).optional(),
            }),
        }),
    ),
});

function readStoredColumnFilters(
    storageKey: string,
): ColumnFiltersState | null {
    try {
        const raw = localStorage.getItem(storageKey);
        if (raw === null) {
            return null;
        }
        const parsed = storedFiltersSchema.safeParse(JSON.parse(raw));
        return parsed.success ? parsed.data.columnFilters : null;
    } catch {
        // Unavailable localStorage or corrupt JSON both fall back to
        // defaults.
        return null;
    }
}

/**
 * Authorizations-page filter state persisted to localStorage, keyed per
 * organization AND per user so coordinators sharing a browser profile — or
 * one user switching organizations — never inherit each other's filters.
 *
 * Filters start at the default (status include chip) and hydrate once both
 * ids are known. Stored values that fail schema validation are discarded.
 */
export function useAuthorizationsFilterPersistence() {
    const organizationId = useOrganizationId();
    const { data: user } = useUser({
        reason: 'authorizations-filter-persistence',
    });
    const storageKey =
        organizationId && user
            ? `${STORAGE_KEY_BASE}:${organizationId}:${user.id}`
            : null;

    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
        DEFAULT_COLUMN_FILTERS,
    );
    // State (not a ref) so React batches it with setColumnFilters in the
    // hydration effect; a ref guard would flip on the same commit as
    // hydration and let the persist effect overwrite the user's stored
    // filters with defaults.
    const [hydratedStorageKey, setHydratedStorageKey] = useState<string | null>(
        null,
    );

    useEffect(() => {
        if (!storageKey || hydratedStorageKey === storageKey) {
            return;
        }
        setColumnFilters(
            readStoredColumnFilters(storageKey) ?? DEFAULT_COLUMN_FILTERS,
        );
        setHydratedStorageKey(storageKey);
    }, [storageKey, hydratedStorageKey]);

    useEffect(() => {
        if (!storageKey || hydratedStorageKey !== storageKey) {
            return;
        }
        try {
            localStorage.setItem(
                storageKey,
                JSON.stringify({ version: 1, columnFilters }),
            );
        } catch {
            // localStorage may be unavailable (private browsing).
        }
    }, [columnFilters, storageKey, hydratedStorageKey]);

    const isHydrated = storageKey !== null && hydratedStorageKey === storageKey;

    return { columnFilters, setColumnFilters, isHydrated };
}
